// Card faces drawn from the card templates (public/cardframes/) and the card's row in cards.csv.
//
// A card is built in layers on a 750 x 1050 canvas, the size of the templates:
//   1. its art, filling the art window (moved and zoomed by art_x / art_y / art_zoom)
//   2. the frame, with its rim tinted by rarity and its name plate and text box by affiliation
//   3. the tier box, the stat circles, the cost gem
//   4. the words: name, type lines, tiers, description, flavor and the set line
// Everything is worked out from the card sheet each time the game starts, so a changed cell shows at once.
import { CONFIG } from '../config.ts';
import { type CardDef, cardStats, costLabel, isEmperor, tierLines, titleCase } from '../shared/cards.ts';

const W = 750;
const H = 1050;
const ART = { x: 48, y: 48, w: 654, h: 576 }; // the art window (the lavender area of the templates)
const TOP_Y = 60; // stat circles along the top…
const BOTTOM_Y = 990; // …and along the bottom
const CIRCLE_R = 61;
const STEP = 120; // from one circle to the next
const GEM = { x: 662, y: 88, r: 88 }; // the cost gem
// gemRing: a ring drawn round the cost gem where the template's own gem picture has none (Ancient: green, Grand: black), in the color of its stat circles' ring.
// What differs between the three templates (common, Grand, Ancient). panelLum: how light the template's own
// name plate and text box are, so an affiliation color comes out as light on each.
// bright: how light the panels come out next to a common card's. print: the description's color on the template.
const BASES = {
  common: { tierY: 24, panelLum: 67.4, bright: 1, icon: '#000000', tierInk: '#0d0d0f', tierHalo: 'rgba(255,255,255,0.35)', gemOutline: 0, gemRing: null as string | null, print: 'dark' },
  grand: { tierY: 21, panelLum: 60.2, bright: 1, icon: '#000000', tierInk: '#f4f4f4', tierHalo: 'rgba(0,0,0,0.75)', gemOutline: 0, gemRing: '#000000' as string | null, print: 'light' },
  ancient: { tierY: 14, panelLum: 16.6, bright: 0.7, icon: '#13552e', tierInk: '#1d8a4a', tierHalo: 'rgba(0,0,0,0.8)', gemOutline: 3, gemRing: '#13552e' as string | null, print: 'light' },
};
type Base = keyof typeof BASES;
// the tier box runs on under the cost gem, so it meets the gem's edge with no gap at any height
const TIER_RIGHT = 640;

const FONTS = {
  name: '"Ngetic Modern", "Times New Roman", serif',
  flavor: '"Ngetic Modern Italic", "Times New Roman", serif',
  body: '"Card Serif", "Times New Roman", serif',
  number: '"Card Numbers", "Arial Black", sans-serif',
};
const FONT_FILES: [string, string, FontFaceDescriptors?][] = [
  ['Ngetic Modern', 'NgeticModern-Regular.otf'],
  ['Ngetic Modern Italic', 'NgeticModern-Italic.otf'],
  ['Card Serif', 'LiberationSerif-Regular.ttf'],
  ['Card Serif', 'LiberationSerif-Bold.ttf', { weight: '700' }],
  ['Card Serif', 'LiberationSerif-Italic.ttf', { style: 'italic' }],
  ['Card Serif', 'LiberationSerif-BoldItalic.ttf', { weight: '700', style: 'italic' }],
  ['Card Numbers', 'DejaVuSans-BoldOblique.ttf'],
];
let fontsLoaded: Promise<void> | null = null;
function loadFonts(): Promise<void> {
  fontsLoaded ??= Promise.all(
    FONT_FILES.map(async ([family, file, desc]) => {
      try {
        const face = new FontFace(family, `url(fonts/${file})`, desc);
        document.fonts.add(await face.load());
      } catch {
        console.warn(`Font not found: public/fonts/${file}`);
      }
    }),
  ).then(() => undefined);
  return fontsLoaded;
}

const images = new Map<string, Promise<HTMLImageElement | null>>();
/**
 * The names a picture may really have on disk: the one asked for, then the same name with its ending in other
 * cases and formats (art saved as "ashbeetle.PNG" or "mirage.jpg" when the sheet says "ashbeetle"). The online
 * server, like most, tells "a.png" and "a.PNG" apart.
 */
export function pictureNames(file: string): string[] {
  const m = /\.(png|jpe?g|webp)$/i.exec(file);
  if (!m) return [file];
  const stem = file.slice(0, -m[0].length);
  return [...new Set([file, ...['.png', '.PNG', '.jpg', '.JPG', '.jpeg', '.JPEG', '.webp'].map((e) => stem + e)])];
}
/** The lighter copy the online build made of a picture, if any (its list is kept in lower case: see scripts/build.mjs). */
export function lighterOf(file: string): string | undefined {
  return typeof __LIGHTER__ !== 'undefined' && __LIGHTER__ ? __LIGHTER__[file.toLowerCase()] : undefined;
}
function picture(file: string): Promise<HTMLImageElement | null> {
  let p = images.get(file);
  if (!p) {
    // the online build keeps lighter copies of big pictures (card art): use one if there is one; then the
    // picture itself, under each name it might have
    const tries = [lighterOf(file), ...pictureNames(file)].filter((f): f is string => !!f);
    p = new Promise((done) => {
      const img = new Image();
      let k = 0;
      img.onload = () => done(img);
      img.onerror = () => {
        if (++k < tries.length) img.src = tries[k];
        else done(null);
      };
      img.src = tries[0];
    });
    images.set(file, p);
  }
  return p;
}
const frameFile = (name: string) => CONFIG.cardFaces.folder + name;
/** A template piece from a set's own folder (Bones & Bruises: bab/), or the Base Set's when that set has none. */
function piece(kit: string, name: string): Promise<HTMLImageElement | null> {
  if (!kit) return picture(frameFile(name));
  return picture(frameFile(kit + name)).then((img) => img ?? picture(frameFile(name)));
}
/** What differs for a set's templates (CONFIG.sets): Bones & Bruises' gems carry their own ring, and its tier plate sits a little lower. */
const KITS: Record<string, { gemRing?: null; tierY?: number }> = { 'bab/': { gemRing: null, tierY: 25 } };

function canvas(w = W, h = H): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d', { willReadFrequently: true })!];
}
function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16) || 0) as [number, number, number];
}

// ---------------- what a card looks like ----------------

interface Look {
  base: Base; // which template
  kit: string; // the folder of the card's set's templates ('' = the Base Set's, 'bab/' = Bones & Bruises')
  rim: string | null; // tint for the rim (null = as the template is)
  stone: string | null; // tint for the outer stone
  panels: string[]; // the name plate and text box: one color, or two or three side by side (one for each affiliation)
  panelsFixed: boolean; // the panel color is the template's own (Triggers), not an affiliation
  ink: string; // name, type lines, numbers
  disc: string | null; // tint for the stat circles (null = as the template is)
}
function lookOf(card: CardDef): Look | null {
  if (isEmperor(card)) return null; // Emperors have a template of their own (not here yet)
  const F = CONFIG.cardFaces;
  const aff = CONFIG.placeholderCards.affiliation;
  const affColors = card.affiliations.map((a) => aff[a]).filter((c): c is string => !!c);
  const panels = affColors.length ? affColors.slice(0, 3) : [CONFIG.placeholderCards.noAffiliation];
  const kit = CONFIG.sets[card.set]?.frames ?? '';
  const style = card.spawnOnly ? F.styles.summon : card.types.includes('trigger') ? F.styles.trigger : null;
  if (style) {
    return { base: 'common', kit, rim: style.rim ?? null, stone: style.stone ?? null, panels: style.panels ? [style.panels] : panels, panelsFixed: !!style.panels, ink: style.ink, disc: style.disc ?? null };
  }
  if (card.rarity === 'grand' || card.rarity === 'ancient') {
    return { base: card.rarity, kit, rim: null, stone: null, panels, panelsFixed: false, ink: F.ink[card.rarity], disc: null };
  }
  const rarity = CONFIG.placeholderCards.rarity[card.rarity];
  const common = card.rarity === 'common' || !rarity;
  return { base: 'common', kit, rim: common ? null : rarity, stone: null, panels, panelsFixed: false, ink: F.ink[card.rarity] ?? (common ? F.ink.common : rarity), disc: null };
}

// ---------------- the frame, tinted (kept per look) ----------------

const frames = new Map<string, Promise<HTMLCanvasElement | null>>();
function tintedFrame(look: Look): Promise<HTMLCanvasElement | null> {
  const key = JSON.stringify([look.kit, look.base, look.rim, look.stone, look.panels]);
  let p = frames.get(key);
  if (p) return p;
  p = (async () => {
    const [frame, zones] = await Promise.all([piece(look.kit, `${look.base}_frame.png`), piece(look.kit, `${look.base}_zones.png`)]);
    if (!frame || !zones) return null;
    const [c, ctx] = canvas();
    ctx.drawImage(frame, 0, 0, W, H);
    const [, zctx] = canvas();
    zctx.drawImage(zones, 0, 0, W, H);
    const img = ctx.getImageData(0, 0, W, H);
    const z = zctx.getImageData(0, 0, W, H).data;
    const d = img.data;
    const T = CONFIG.cardFaces.tint;
    const rim = look.rim ? rgb(look.rim).map((v) => v / T.rim) : null;
    const stone = look.stone ? rgb(look.stone).map((v) => v / T.stone) : null;
    const lift = (BASES.common.panelLum / BASES[look.base].panelLum) * BASES[look.base].bright;
    const pa = look.panels.map((h) => rgb(h).map((v) => (v / T.panels) * lift));
    for (let y = 0; y < H; y++) {
      // two or three affiliations: side by side in equal bands, blended across each seam (Calling Pillar: three)
      const left = pa[0];
      const n = pa.length;
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4;
        const zr = z[i] / 255;
        const zs = z[i + 1] / 255;
        const zp = z[i + 2] / 255;
        if (!zr && !zs && !zp) continue;
        let r = d[i];
        let g = d[i + 1];
        let b = d[i + 2];
        let nr = r;
        let ng = g;
        let nb = b;
        if (zr && rim) {
          nr += (r * rim[0] - r) * zr;
          ng += (g * rim[1] - g) * zr;
          nb += (b * rim[2] - b) * zr;
        }
        if (zs && stone) {
          nr += (r * stone[0] - r) * zs;
          ng += (g * stone[1] - g) * zs;
          nb += (b * stone[2] - b) * zs;
        }
        if (zp) {
          const lum = 0.3 * r + 0.59 * g + 0.11 * b;
          let col = left;
          if (n > 1) {
            // the band this pixel is in, and how far across the nearest seam (24 px wide) it is
            const j = Math.min(n - 1, Math.max(1, Math.round((x / W) * n)));
            const t = Math.min(1, Math.max(0, (x - (W * j) / n + 12) / 24));
            col = pa[j - 1].map((v, k) => v + (pa[j][k] - v) * t);
          }
          nr += (lum * col[0] - nr) * zp;
          ng += (lum * col[1] - ng) * zp;
          nb += (lum * col[2] - nb) * zp;
        }
        d[i] = nr;
        d[i + 1] = ng;
        d[i + 2] = nb;
      }
    }
    ctx.putImageData(img, 0, 0);
    return c;
  })();
  frames.set(key, p);
  return p;
}

// ---------------- pieces ----------------

/**
 * The border along the top and bottom of a tier plate, read from its body picture: its colour, how thick it is,
 * and the first and last rows of the plate. Used to close off the plate's left end when nothing covers it.
 */
const edges = new WeakMap<HTMLImageElement, { color: string; width: number; top: number; bottom: number } | null>();
function plateEdge(body: HTMLImageElement) {
  if (edges.has(body)) return edges.get(body)!;
  const [c, ctx] = canvas(body.width, body.height);
  ctx.drawImage(body, 0, 0);
  const x = Math.floor(body.width / 2);
  const px = (y: number) => ctx.getImageData(x, y, 1, 1).data;
  let top = 0;
  while (top < body.height && px(top)[3] < 128) top++;
  let bottom = body.height - 1;
  while (bottom > top && px(bottom)[3] < 128) bottom--;
  if (top >= bottom) {
    edges.set(body, null);
    return null;
  }
  const [r, g, b] = px(top);
  let width = 1;
  while (top + width < bottom) {
    const [r2, g2, b2] = px(top + width);
    if (Math.abs(r2 - r) + Math.abs(g2 - g) + Math.abs(b2 - b) > 40) break;
    width++;
  }
  void c;
  const edge = { color: `rgb(${r}, ${g}, ${b})`, width: Math.max(2, Math.min(width, 6)), top, bottom };
  edges.set(body, edge);
  return edge;
}

/** A stat circle, tinted for card kinds whose circles have a color of their own (summoned units: tan). */
const discs = new Map<string, Promise<HTMLCanvasElement | HTMLImageElement | null>>();
function statDisc(base: Base, tint: string | null, kit = '') {
  const key = `${kit}${base}:${tint}`;
  let p = discs.get(key);
  if (!p) {
    p = piece(kit, `${base}_disc.png`).then((img) => {
      if (!img || !tint) return img;
      const [c, ctx] = canvas(img.width, img.height);
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.width, img.height);
      const k = rgb(tint).map((v) => v / CONFIG.cardFaces.tint.stone);
      for (let i = 0; i < data.data.length; i += 4) for (let j = 0; j < 3; j++) data.data[i + j] = data.data[i + j] * k[j];
      ctx.putImageData(data, 0, 0);
      return c;
    });
    discs.set(key, p);
  }
  return p;
}

/**
 * A cost gem. A card paid for with Dark Gerzite (cost_type dark) has a dark red glow around its gem instead of
 * the blue one: the blue of the glow is turned to dark red, everything else is left as it is.
 */
const gems = new Map<string, Promise<HTMLCanvasElement | HTMLImageElement | null>>();
function costGem(base: Base, dark: boolean, small = false, kit = '') {
  const key = `${kit}${base}:${dark}:${small}`;
  let p = gems.get(key);
  if (!p) {
    p = piece(kit, `${base}_${small ? 'smallgem' : 'gem'}.png`).then((img) => {
      if (!img || !dark) return img;
      const [c, ctx] = canvas(img.width, img.height);
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.width, img.height);
      const d = data.data;
      const [gr, gg, gb] = rgb(CONFIG.cardFaces.darkGlow);
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i];
        const g = d[i + 1];
        const b = d[i + 2];
        const blue = Math.min(1, Math.max(0, (b - r - 6) / 30)); // how much of the glow is in this pixel
        if (!blue) continue;
        const lum = (0.3 * r + 0.59 * g + 0.11 * b) / 95;
        d[i] = r + (Math.min(255, gr * lum * 1.15) - r) * blue;
        d[i + 1] = g + (Math.min(255, gg * lum * 1.15) - g) * blue;
        d[i + 2] = b + (Math.min(255, gb * lum * 1.15) - b) * blue;
      }
      ctx.putImageData(data, 0, 0);
      return c;
    });
    gems.set(key, p);
  }
  return p;
}

/** A white icon, colored. */
const icons = new Map<string, Promise<HTMLCanvasElement | null>>();
function icon(name: string, color: string): Promise<HTMLCanvasElement | null> {
  const key = `${name}:${color}`;
  let p = icons.get(key);
  if (!p) {
    p = picture(frameFile(`icons/${name}.png`)).then((img) => {
      if (!img) return null;
      const [c, ctx] = canvas(img.width, img.height);
      ctx.drawImage(img, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, img.width, img.height);
      return c;
    });
    icons.set(key, p);
  }
  return p;
}

/** A number the way the templates print them: heavy italic, outlined in black, right edge at x. */
function number(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, fill: string, outline: number, maxW: number, align: CanvasTextAlign = 'right') {
  let s = size;
  ctx.font = `${s}px ${FONTS.number}`;
  while (s > 18 && ctx.measureText(text).width > maxW) {
    s -= 2;
    ctx.font = `${s}px ${FONTS.number}`;
  }
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  if (outline) {
    ctx.strokeStyle = '#000';
    ctx.lineWidth = outline * 2;
    ctx.strokeText(text, x, y);
  }
  // a light top fading to the ink color, like the templates' numbers
  const g = ctx.createLinearGradient(0, y - s * 0.75, 0, y);
  g.addColorStop(0, shade(fill, 1.35));
  g.addColorStop(1, fill);
  ctx.fillStyle = g;
  ctx.fillText(text, x, y);
}
function shade(hex: string, k: number) {
  const [r, g, b] = rgb(hex).map((v) => Math.min(255, Math.round(k >= 1 ? v + (255 - v) * (k - 1) : v * k)));
  return `rgb(${r},${g},${b})`;
}

/** Text with a black outline (the name and the type lines). */
function outlined(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fill: string, outline: number) {
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = outline * 2;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

type Stat = { icon: string; value: string };
/** Which stats go in the top row and which along the bottom, for this card. */
function statRows(card: CardDef): { top: Stat[]; bottom: Stat[]; smallGem: Stat | null } {
  const stats = cardStats(card).filter((s) => s.value !== '0');
  const pick = (names: string[]) => names.flatMap((n) => stats.filter((s) => s.icon === n)).map((s) => ({ icon: s.icon, value: s.value }));
  // a Tome's replay cost goes in a circle with the cycle arrows, a Biome's removal cost in one with a "no" sign
  const bottom = pick(['thorns', 'range', 'damage', 'replay', 'removal']);
  const top = pick(['health', 'sp', 'shield', 'buffer', 'count', 'heals', 'turns']);
  return { top, bottom, smallGem: null };
}

// ---------------- the card ----------------

const faces = new Map<string, Promise<string | null>>();
/** The card's face as an image URL, or null if it has no template yet (then the older look is used). */
export function cardFaceSrc(card: CardDef, dark = false, form = 0): Promise<string | null> {
  if (!card.formImages?.[form]) form = 0; // (only a card with its own art for that form looks any different)
  const key = `${card.number}:${dark}:${form}`;
  let p = faces.get(key);
  if (!p) {
    p = drawFace(card, dark, form).then((c) => (c ? new Promise<string>((done) => c.toBlob((b) => done(b ? URL.createObjectURL(b) : c.toDataURL()), 'image/webp', 0.92)) : null));
    faces.set(key, p);
  }
  return p;
}
export function hasFace(card: CardDef): boolean {
  return CONFIG.cardFaces.enabled && (isEmperor(card) || !!lookOf(card));
}

async function drawFace(card: CardDef, summonedDark = false, form = 0): Promise<HTMLCanvasElement | null> {
  if (isEmperor(card)) return drawEmperor(card);
  const look = lookOf(card);
  if (!look) return null;
  const F = CONFIG.cardFaces;
  const rows = statRows(card);
  const [frame, art, disc, gem, smallGem, tierBody, tierCap] = await Promise.all([
    tintedFrame(look),
    // (a two-cost card summoned at its second cost may have its own art: Forgotten Monarch)
    ((img) => (img ? picture(F.artFolder + (/\.\w+$/.test(img) ? img : `${img}.png`)) : Promise.resolve(null)))((form > 0 && card.formImages?.[form]) || card.image),
    statDisc(look.base, look.disc, look.kit),
    card.spawnOnly ? Promise.resolve(null) : costGem(look.base, card.costDark || summonedDark, false, look.kit),
    rows.smallGem ? costGem(look.base, false, true, look.kit) : Promise.resolve(null),
    piece(look.kit, `${look.base}_tierbody.png`),
    piece(look.kit, `${look.base}_tiercap.png`),
    loadFonts(),
  ]);
  if (!frame) return null;
  const [c, ctx] = canvas();

  // 1. the art
  drawArt(ctx, card, art);

  // 2. the frame
  ctx.drawImage(frame, 0, 0);

  // 3. tier box, stat circles, gems
  const { top, bottom, smallGem: small } = rows;
  const tiers = tierLines(card);
  const hasBox = tiers.length > 0 && !!tierBody && !!tierCap;
  // the top row leans against the tier box (ending at x 300); without one it starts in the corner
  const n = top.length;
  const spacing = n > 3 ? (hasBox ? 240 : 360) / (n - 1) : STEP;
  const topX = top.map((_, i) => (hasBox ? 300 - (n - 1 - i) * spacing : 60 + i * spacing));
  if (hasBox) {
    const left = n ? topX[n - 1] : 240;
    const tierY = KITS[look.kit]?.tierY ?? BASES[look.base].tierY;
    for (let x = TIER_RIGHT; x > left; x -= tierBody!.width) {
      const w = Math.min(tierBody!.width, x - left);
      ctx.drawImage(tierBody!, tierBody!.width - w, 0, w, tierBody!.height, x - w, tierY, w, tierBody!.height);
    }
    ctx.drawImage(tierCap!, TIER_RIGHT, tierY);
    ctx.save(); // the left end: the right end, mirrored
    ctx.translate(left, tierY);
    ctx.scale(-1, 1);
    ctx.drawImage(tierCap!, 0, 0);
    ctx.restore();
    // with no stat circle over its left end (a card with no stats in its top row, like Granduer Potion) that
    // end shows: it gets the same border as the plate's top and bottom edges
    if (!n) {
      const edge = plateEdge(tierBody!);
      if (edge) {
        ctx.fillStyle = edge.color;
        ctx.fillRect(left - tierCap!.width, tierY + edge.top, edge.width, edge.bottom - edge.top + 1);
      }
    }
    // its lines
    ctx.font = `700 17px ${FONTS.body}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const tx = (n ? topX[n - 1] + CIRCLE_R : left + 14) + 8;
    const maxW = 572 - 8 - tx; // the words stop where the gem begins
    let size = 17;
    while (size > 11 && tiers.some((t) => ctx.measureText(t).width > maxW)) ctx.font = `700 ${--size}px ${FONTS.body}`;
    tiers.forEach((t, i) => {
      const y = tierY + tierBody!.height / 2 + (i - (tiers.length - 1) / 2) * (size + 3);
      ctx.lineWidth = 3;
      ctx.strokeStyle = BASES[look.base].tierHalo;
      ctx.strokeText(t, tx, y);
      ctx.fillStyle = BASES[look.base].tierInk;
      ctx.fillText(t, tx, y);
    });
  }
  const circle = async (s: Stat, cx: number, cy: number) => {
    if (disc) ctx.drawImage(disc, cx - disc.width / 2, cy - disc.height / 2);
    const ic = await icon(s.icon, BASES[look.base].icon);
    if (ic) ctx.drawImage(ic, cx - ic.width / 2, cy - ic.height / 2);
    number(ctx, s.value, cx + 58, cy + 58, 44, look.ink, 3, 92);
  };
  // left circles sit over the ones to their right along the top; along the bottom it's the other way round
  for (let i = n - 1; i >= 0; i--) await circle(top[i], topX[i], TOP_Y);
  const bottomRow = small ? [] : bottom;
  const bx = bottomRow.map((_, i) => 690 - (bottomRow.length - 1 - i) * STEP);
  for (let i = 0; i < bottomRow.length; i++) await circle(bottomRow[i], bx[i], BOTTOM_Y);
  if (small && smallGem) {
    ctx.drawImage(smallGem, 690 - smallGem.width / 2, BOTTOM_Y - smallGem.height / 2);
    number(ctx, small.value, 690, BOTTOM_Y + 15, 40, look.ink, 0, 70, 'center');
  }
  if (!card.spawnOnly && gem) {
    ctx.drawImage(gem, GEM.x - gem.width / 2, GEM.y - gem.height / 2);
    // the Ancient and Grand templates' gems have no outline of their own: each gets the same ring as its stat circles (green, black)
    const ring = look.kit in KITS && KITS[look.kit].gemRing === null ? null : BASES[look.base].gemRing;
    if (ring) {
      const width = 4.5;
      ctx.save();
      ctx.beginPath();
      ctx.arc(GEM.x, GEM.y, gem.width / 2 - 3 - width / 2, 0, Math.PI * 2);
      ctx.lineWidth = width;
      ctx.strokeStyle = ring;
      ctx.stroke();
      ctx.restore();
    }
    number(ctx, costLabel(card), GEM.x, GEM.y + 17, 48, look.ink, BASES[look.base].gemOutline, 90, 'center');
  }

  // 4. words
  // the type lines: the type, then the card's descriptors. Tomes, Biomes and Triggers say only their type.
  const typeName = card.spawnOnly ? 'Summoned Troop' : titleCase(card.types.join('/'));
  const plain = card.types.some((t) => t === 'tome' || t === 'biome' || t === 'trigger');
  const lines = plain ? [typeName] : [typeName, ...card.traits.flatMap((t) => t.split('|')).map((t) => t.trim()).filter(Boolean)];
  let ts = 19;
  const lineGap = () => (lines.length > 4 ? 72 / lines.length : 18.7);
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.font = `${ts}px ${FONTS.name}`;
  while (ts > 12 && (lines.length > 4 ? lines.length * ts * 0.95 > 76 : false)) ctx.font = `${--ts}px ${FONTS.name}`;
  const typeW = Math.max(...lines.map((l) => ctx.measureText(l).width));
  lines.forEach((l, i) => outlined(ctx, l, 657, 680 + (i - (lines.length - 1) / 2) * lineGap(), look.ink, 1.6));
  // the name, as large as fits beside them
  let ns = 46;
  ctx.textAlign = 'left';
  ctx.font = `${ns}px ${FONTS.name}`;
  const roomForName = 657 - typeW - 22 - 92;
  while (ns > 22 && ctx.measureText(card.name).width > roomForName) ctx.font = `${--ns}px ${FONTS.name}`;
  outlined(ctx, card.name, 92, 682, look.ink, 3);

  // the description and flavor: black, as on the templates, unless the panel is too dark to read it
  // (how light the panel really comes out: the template's own lightness, lifted to a common card's)
  const panelLum = Math.min(...look.panels.map((h) => { const [r, g, b] = rgb(h); return ((0.3 * r + 0.59 * g + 0.11 * b) * BASES.common.panelLum * BASES[look.base].bright) / CONFIG.cardFaces.tint.panels; }));
  const light = BASES[look.base].print === 'light' || panelLum < CONFIG.cardFaces.lightTextBelow;
  // (light print on a pale panel gets a firmer halo, so it still stands out)
  const print = light ? { fill: '#f4f4f6', halo: panelLum > 120 ? 'rgba(0,0,0,0.95)' : panelLum > 60 ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.55)' } : { fill: '#0d0d0f', halo: '' };
  // the description, as large as fits above the flavor line
  const bottomLeft = bottomRow.length || small ? (bx[0] ?? 690) - CIRCLE_R - 8 : 690;
  const flavorLines = card.flavor ? wrap(ctx, card.flavor, `16px ${FONTS.flavor}`, bottomLeft - 88) : [];
  const flavorTop = 972 - flavorLines.length * 18;
  drawDescription(ctx, card.text, 74, 772, 676 - 74, flavorTop - 10 - 772, print);
  ctx.font = `16px ${FONTS.flavor}`;
  ctx.fillStyle = print.fill;
  ctx.shadowColor = print.halo || 'transparent';
  ctx.shadowBlur = print.halo ? 3 : 0;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  flavorLines.forEach((l, i) => ctx.fillText(l, 88, flavorTop + 14 + i * 18));
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';

  // the set line
  const set = CONFIG.sets[card.set] ?? CONFIG.sets.base;
  const setLine = card.spawnOnly ? `ARELLIX  -  ${F.year}  -  ${set.line}` : `ARELLIX  -  ${F.year}  -  ${set.line} ${String(card.setNumber ?? card.number).padStart(set.size >= 100 ? 3 : 2, '0')}/${set.size}`;
  ctx.font = `italic 700 11px ${FONTS.body}`;
  ctx.fillStyle = look.ink === F.ink.common ? '#bdbec4' : shade(look.ink, 1.25);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0.6px';
  ctx.fillText(setLine, 137, 1017);
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0px';
  return c;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, font: string, maxW: number): string[] {
  ctx.font = font;
  const out: string[] = [];
  for (const para of text.split('\n')) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(next).width > maxW) {
        out.push(line);
        line = word;
      } else line = next;
    }
    if (line) out.push(line);
  }
  return out;
}

/** The description: black serif, a leading "Passive:" (and the like) in bold, as large as fits the box. */
function drawDescription(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, print: { fill: string; halo: string }, maxSize = 22) {
  if (!text) return;
  const labels = CONFIG.cardText.labels;
  const paras = text.split('\n').map((p) => {
    const m = p.match(/^([A-Za-z]+):\s*(.*)$/);
    return m && labels.includes(m[1]) ? { bold: `${m[1]}:`, rest: m[2] } : { bold: '', rest: p };
  });
  for (let size = maxSize; size >= 12; size--) {
    const lineH = size * 1.18;
    const lines: { bold: string; words: string }[] = [];
    for (const p of paras) {
      ctx.font = `700 ${size}px ${FONTS.body}`;
      const boldW = p.bold ? ctx.measureText(`${p.bold} `).width : 0;
      ctx.font = `${size}px ${FONTS.body}`;
      let line = '';
      let first = true;
      for (const word of p.rest.split(/\s+/).filter(Boolean)) {
        const next = line ? `${line} ${word}` : word;
        const room = w - (first ? boldW : 0);
        if (line && ctx.measureText(next).width > room) {
          lines.push({ bold: first ? p.bold : '', words: line });
          first = false;
          line = word;
        } else line = next;
      }
      lines.push({ bold: first ? p.bold : '', words: line });
    }
    if (lines.length * lineH > h && size > 12) continue;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = print.fill;
    ctx.shadowColor = print.halo || 'transparent';
    ctx.shadowBlur = print.halo ? 3 : 0;
    lines.forEach((l, i) => {
      const by = y + size + i * lineH;
      let cx = x;
      if (l.bold) {
        ctx.font = `700 ${size}px ${FONTS.body}`;
        ctx.fillText(l.bold, cx, by);
        cx += ctx.measureText(`${l.bold} `).width;
      }
      ctx.font = `${size}px ${FONTS.body}`;
      ctx.fillText(l.words, cx, by);
    });
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    return;
  }
}

// ---------------- Emperors ----------------
// The Emperor template is used as it is (its own colors, no tinting): its heart and sword circles, its two
// ability gems and its words are drawn on it. A Shield or Buffer joins the heart along the top, Thorns the sword.
const EMPEROR = { icon: '#64691f', outline: '#3f4214', gems: [813, 928], gemX: 103, gemR: 43 };

/**
 * The stone ring around the very edge of the Emperor template: every pixel that can be reached from the card's
 * edge without crossing the olive border lines (found once, from the template itself).
 */
let edgeMask: Promise<Uint8Array | null> | null = null;
function emperorEdge(frame: HTMLImageElement): Promise<Uint8Array | null> {
  if (edgeMask) return edgeMask;
  edgeMask = (async () => {
    const [, ctx] = canvas();
    ctx.drawImage(frame, 0, 0, W, H);
    const d = ctx.getImageData(0, 0, W, H).data;
    const wall = (i: number) => d[i * 4 + 3] < 128 || (d[i * 4 + 2] < 0.6 * d[i * 4 + 1] && d[i * 4 + 1] > 60); // see-through, or an olive line
    const mask = new Uint8Array(W * H);
    const stack: number[] = [];
    for (let x = 0; x < W; x++) stack.push(x, (H - 1) * W + x);
    for (let y = 0; y < H; y++) stack.push(y * W, y * W + W - 1);
    while (stack.length) {
      const i = stack.pop()!;
      if (mask[i] || wall(i)) continue;
      mask[i] = 1;
      const x = i % W;
      if (x > 0) stack.push(i - 1);
      if (x < W - 1) stack.push(i + 1);
      if (i >= W) stack.push(i - W);
      if (i < W * (H - 1)) stack.push(i + W);
    }
    return mask;
  })();
  return edgeMask;
}

/** The Emperor template with its edge ring in the Emperor's affiliation color (kept per affiliation). */
const emperorFrames = new Map<string, Promise<HTMLCanvasElement | HTMLImageElement | null>>();
function emperorFrame(card: CardDef) {
  const aff = CONFIG.placeholderCards.affiliation;
  const colors = card.affiliations.map((a) => aff[a]).filter((c): c is string => !!c).slice(0, 2);
  const key = colors.join('|');
  let p = emperorFrames.get(key);
  if (p) return p;
  p = (async () => {
    const frame = await picture(frameFile('emperor_frame.png'));
    if (!frame || !colors.length) return frame;
    const mask = await emperorEdge(frame);
    if (!mask) return frame;
    const [c, ctx] = canvas();
    ctx.drawImage(frame, 0, 0, W, H);
    const img = ctx.getImageData(0, 0, W, H);
    const d = img.data;
    // the ring's own light and dark (its cracks) are kept: each pixel is the color, as light as the stone there
    let sum = 0;
    let n = 0;
    for (let i = 0; i < mask.length; i++) if (mask[i]) { sum += 0.3 * d[i * 4] + 0.59 * d[i * 4 + 1] + 0.11 * d[i * 4 + 2]; n++; }
    const mean = sum / Math.max(1, n);
    const lift = CONFIG.cardFaces.emperor.edgeLift;
    const cols = colors.map(rgb);
    for (let i = 0; i < mask.length; i++) {
      if (!mask[i]) continue;
      const x = i % W;
      const t = cols.length > 1 ? Math.min(1, Math.max(0, (x - W / 2 + 12) / 24)) : 0;
      const col = cols.length > 1 ? cols[0].map((v, k) => v + (cols[1][k] - v) * t) : cols[0];
      const k = ((0.3 * d[i * 4] + 0.59 * d[i * 4 + 1] + 0.11 * d[i * 4 + 2]) / mean) * lift;
      d[i * 4] = col[0] * k;
      d[i * 4 + 1] = col[1] * k;
      d[i * 4 + 2] = col[2] * k;
    }
    ctx.putImageData(img, 0, 0);
    return c;
  })();
  emperorFrames.set(key, p);
  return p;
}

/** An ability's cost gem, drawn fresh over the template's: an olive ring, a pale stone disc, a centered olive hexagon. */
function abilityGem(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const G = CONFIG.cardFaces.emperor.gem;
  const R = EMPEROR.gemR;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = G.ring;
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  const disc = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.35, R * 0.1, cx, cy, R * 0.86);
  disc.addColorStop(0, shade(G.disc, 1.25));
  disc.addColorStop(0.7, G.disc);
  disc.addColorStop(1, G.discDark);
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.83, 0, Math.PI * 2);
  ctx.fillStyle = disc;
  ctx.fill();
  ctx.beginPath();
  const hr = R * 0.6;
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 3) * k;
    const x = cx + hr * Math.cos(a);
    const y = cy + hr * Math.sin(a);
    if (k) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = G.hex;
  ctx.fill();
  ctx.restore();
}

/** A number centered on a point both ways, by the drawn digits' own height. */
function centeredNumber(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, fill: string, outline: number, maxW: number) {
  let s = size; // as number() sizes it
  ctx.font = `${s}px ${FONTS.number}`;
  while (s > 18 && ctx.measureText(text).width > maxW) ctx.font = `${(s -= 2)}px ${FONTS.number}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const m = ctx.measureText(text);
  const dx = (m.actualBoundingBoxLeft - m.actualBoundingBoxRight) / 2;
  const dy = (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  number(ctx, text, x + dx, y + dy, size, fill, outline, maxW, 'left');
}
async function drawEmperor(card: CardDef): Promise<HTMLCanvasElement | null> {
  const F = CONFIG.cardFaces;
  const ink = CONFIG.placeholderCards.emperor.main;
  const [frame, art, disc] = await Promise.all([
    emperorFrame(card),
    card.image ? picture(F.artFolder + (/\.\w+$/.test(card.image) ? card.image : `${card.image}.png`)) : Promise.resolve(null),
    picture(frameFile('emperor_disc.png')),
    loadFonts(),
  ]);
  if (!frame) return null;
  const [c, ctx] = canvas();
  drawArt(ctx, card, art);
  ctx.drawImage(frame, 0, 0);

  // stat circles: health (and shield, buffer) from the top-left corner, damage (and thorns) from the top-right
  const stats = cardStats(card).filter((st) => st.value !== '0');
  const pick = (names: string[]) => names.flatMap((n) => stats.filter((st) => st.icon === n));
  const left = pick(['health', 'shield', 'buffer']);
  const right = pick(['thorns', 'range', 'damage']);
  const circle = async (st: { icon: string; value: string }, cx: number) => {
    if (disc) ctx.drawImage(disc, cx - disc.width / 2, TOP_Y - disc.height / 2);
    const ic = await icon(st.icon, EMPEROR.icon);
    if (ic) ctx.drawImage(ic, cx - ic.width / 2, TOP_Y - ic.height / 2);
    number(ctx, st.value, cx + 58, TOP_Y + 58, 44, ink, 3, 92);
  };
  for (let i = left.length - 1; i >= 0; i--) await circle(left[i], 60 + i * STEP);
  for (let i = 0; i < right.length; i++) await circle(right[i], 690 - (right.length - 1 - i) * STEP);

  // the name plate: the name large, the title ("The Shieldmaiden") small after it
  const m = card.name.match(/^(.*?)\s+(The\s.*)$/);
  const name = m ? m[1] : card.name;
  const title = m ? m[2] : '';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  let ns = 50;
  const titleFont = (z: number) => `${z}px ${FONTS.name}`;
  ctx.font = `${ns}px ${FONTS.name}`;
  const room = 657 - 92;
  ctx.font = titleFont(20);
  const titleW = title ? ctx.measureText(title).width + 12 : 0;
  ctx.font = `${ns}px ${FONTS.name}`;
  while (ns > 24 && ctx.measureText(name).width + titleW > room) ctx.font = `${--ns}px ${FONTS.name}`;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = EMPEROR.outline;
  ctx.lineWidth = 5;
  ctx.strokeText(name, 92, 697);
  ctx.fillStyle = ink;
  ctx.fillText(name, 92, 697);
  if (title) {
    const nx = 92 + ctx.measureText(name).width + 12;
    ctx.font = titleFont(20);
    // the title ("The Burnt Husk") in the same blue as the name, with the same dark outline so it reads on the gold
    ctx.lineWidth = 3;
    ctx.strokeText(title, nx, 697);
    ctx.fillStyle = ink;
    ctx.fillText(title, nx, 697);
  }

  // the abilities: cost in the gem, name beside it, what it does under the name
  for (let k = 0; k < EMPEROR.gems.length; k++) {
    const ab = card.emperorAbilities.find((a) => a.slot === k + 1);
    if (!ab) continue;
    const gy = EMPEROR.gems[k];
    abilityGem(ctx, EMPEROR.gemX, gy);
    centeredNumber(ctx, ab.costVaries ? '?' : String(ab.cost), EMPEROR.gemX, gy, 34, ab.dark ? F.darkGlow : ink, 2.5, 46);
    ctx.textAlign = 'left';
    ctx.font = `26px ${FONTS.name}`;
    outlined(ctx, ab.name, 155, gy - 18, ink, 2);
    const body = ab.text + (ab.cooldown > 0 ? ` (${ab.cooldown} turn cooldown)` : '');
    drawDescription(ctx, body, 168, gy - 8, 676 - 168, k === 0 ? 878 - (gy - 8) : 996 - (gy - 8), { fill: '#0d0d0f', halo: '' }, 19);
  }

  const set = CONFIG.sets[card.set] ?? CONFIG.sets.base;
  const setLine = `ARELLIX  -  ${F.year}  -  ${set.line} ${String(card.setNumber ?? card.number).padStart(set.size >= 100 ? 3 : 2, '0')}/${set.size}`;
  ctx.font = `italic 700 11px ${FONTS.body}`;
  ctx.fillStyle = ink;
  ctx.textAlign = 'center';
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0.6px';
  ctx.fillText(setLine, W / 2, 1013);
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0px';
  return c;
}

/** The art, covering the window, then moved and zoomed as the sheet says; an empty dark window without art. */
function drawArt(ctx: CanvasRenderingContext2D, card: CardDef, art: HTMLImageElement | null) {
  if (art) {
    const k = Math.max(ART.w / art.width, ART.h / art.height) * (card.art.zoom / 100);
    const w = art.width * k;
    const h = art.height * k;
    ctx.drawImage(art, ART.x + (ART.w - w) / 2 + card.art.x, ART.y + (ART.h - h) / 2 + card.art.y, w, h);
    return;
  }
  const g = ctx.createRadialGradient(W / 2, 330, 40, W / 2, 330, 420);
  g.addColorStop(0, '#2c3035');
  g.addColorStop(1, '#121315');
  ctx.fillStyle = g;
  ctx.fillRect(ART.x, ART.y, ART.w, ART.h);
}
